import { useEffect, useState } from "react";
import { MIN_MOBAI_VERSION, compareVersions, getServerVersion } from "./api";
import { useServerBase } from "./settings";

/**
 * Watches the MobAI desktop app version reported by the server.
 *
 * Returned state:
 *   - `outdated: false` and `version: string` — app is current (or newer).
 *   - `outdated: true, reason: "missing"` — `/api/v1/version` 404'd. The
 *     installed app is old enough that it doesn't know this endpoint yet,
 *     so by definition it's out of date.
 *   - `outdated: true, reason: "below-min"` — server responded but its
 *     version is below MIN_MOBAI_VERSION.
 *   - `outdated: null` — not known yet (still loading, or the server is
 *     unreachable). Callers should treat this as "don't block".
 */
export type VersionState =
  | { outdated: null }
  | { outdated: false; version: string }
  | { outdated: true; reason: "missing" | "below-min"; version: string | null };

export function useServerVersionCheck(): VersionState {
  const [serverBase] = useServerBase();
  const [state, setState] = useState<VersionState>({ outdated: null });

  useEffect(() => {
    let cancelled = false;
    setState({ outdated: null });
    getServerVersion()
      .then((version) => {
        if (cancelled) return;
        if (version === null) {
          setState({ outdated: true, reason: "missing", version: null });
          return;
        }
        if (compareVersions(version, MIN_MOBAI_VERSION) < 0) {
          setState({ outdated: true, reason: "below-min", version });
          return;
        }
        setState({ outdated: false, version });
      })
      .catch(() => {
        // Network error — leave as unknown so we don't block on transient
        // blips. The "Can't reach MobAI" flow surfaces hard outages.
        if (!cancelled) setState({ outdated: null });
      });
    return () => {
      cancelled = true;
    };
  }, [serverBase]);

  return state;
}
